import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { getData, postData, serverURL } from "../../services/FetchNodeservices";
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer 
} from "recharts";
import { 
  FiDownload, FiSearch, FiCalendar, FiUsers, 
  FiDollarSign, FiBriefcase, FiCheckCircle, FiX, FiAward, FiGrid, FiList
} from "react-icons/fi";

export default function DirectorDashboard() {
  // Logged-in User details
  const user = useMemo(() => {
    return JSON.parse(localStorage.getItem("USER")) || {};
  }, []);

  const userId = user?.id || null;

  // Base Data States
  const [teamMembers, setTeamMembers] = useState([]);
  const [positionMatrix, setPositionMatrix] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters State
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedManager, setSelectedManager] = useState("All");
  const [selectedClient, setSelectedClient] = useState("All");
  
  // Search States
  const [recruiterSearch, setRecruiterSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [placementSearch, setPlacementSearch] = useState("");
  const [managerSearch, setManagerSearch] = useState("");

  // Tab State
  const [activeTab, setActiveTab] = useState("recruiter"); // recruiter, manager, client, placement
  
  // Drill-down Modal State
  const [selectedRecruiterDetails, setSelectedRecruiterDetails] = useState(null);
  const [selectedManagerDetails, setSelectedManagerDetails] = useState(null);
  const [activeKpiModal, setActiveKpiModal] = useState(null);
  const [selectedJobCandidates, setSelectedJobCandidates] = useState(null);

  // Load all dashboard data
  const loadData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // 1. Fetch team structure
      const teamRes = await getData(`team-structure/team/${userId}`);
      if (teamRes?.status) {
        setTeamMembers(teamRes.team || []);
      }

      // 2. Fetch Position Matrix / Job Data
      const matrixRes = await getData(`positionmatrix/fetchJobData/${userId}`);
      if (matrixRes?.status) {
        setPositionMatrix(matrixRes.data || []);
      }

      // 3. Fetch Candidates
      const candRes = await getData("candidate");
      if (candRes?.status) {
        setCandidates(candRes.data || []);
      }

      // 4. Fetch Placements
      const placementRes = await getData(`placementtracker/fetch/${userId}`);
      if (placementRes?.status) {
        setPlacements(placementRes.data || []);
      }

      // 5. Fetch Interviews
      const interviewRes = await postData("interview/fetch", { user_id: userId });
      if (interviewRes?.status) {
        setInterviews(interviewRes.data || []);
      }

      // 6. Fetch all users from system
      const usersRes = await postData("users/fetch");
      if (usersRes?.status) {
        setUsersList(usersRes.data || []);
      }

    } catch (error) {
      console.error("Error loading Director dashboard data:", error);
      toast.error("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStatusChange = async (reqId, newStatus) => {
    try {
      const res = await postData("job/update-status", {
        req_id: reqId,
        status: newStatus,
      });
      if (res.status) {
        toast.success(`Status updated successfully`);
        setPositionMatrix(prev =>
          prev.map(row =>
            row.req_id === reqId ? { ...row, final_status: newStatus } : row
          )
        );
      } else {
        toast.error(res.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating status");
    }
  };

  // Derived Manager & Team structure
  const managers = useMemo(() => {
    // A Manager/TL is defined as anyone who has reports in teamMembers
    const reporterManagerIds = new Set(
      teamMembers.map(t => String(t.reportingManagerId)).filter(id => id && id !== "null")
    );
    // Include Director as a Root Manager
    const list = teamMembers.filter(member => reporterManagerIds.has(String(member.userId)));
    return list;
  }, [teamMembers]);

  // Derived Manager list for dropdown selector
  const dropdownManagers = useMemo(() => {
    return teamMembers.filter(member => 
      String(member.designation).toLowerCase() === "manager"
    );
  }, [teamMembers]);

  // Get descendant IDs recursively for any manager userId
  const getTeamUserIds = useCallback((mgrId) => {
    const list = [];
    const directReports = teamMembers.filter(t => String(t.reportingManagerId) === String(mgrId));
    directReports.forEach(report => {
      list.push(Number(report.userId));
      list.push(...getTeamUserIds(report.userId));
    });
    return [...new Set(list)];
  }, [teamMembers]);

  // List of allowed user IDs based on selectedManager filter
  const allowedUserIds = useMemo(() => {
    if (selectedManager === "All") {
      return null; // All users allowed
    }
    const teamIds = getTeamUserIds(selectedManager);
    return [Number(selectedManager), ...teamIds];
  }, [selectedManager, getTeamUserIds]);

  // Helper: check if a record's creator or owner is in the allowed users
  const isUserAllowed = useCallback((ownerId) => {
    if (!allowedUserIds) return true;
    return allowedUserIds.includes(Number(ownerId));
  }, [allowedUserIds]);

  // Helper: check date bounds
  const isDateInBounds = useCallback((dateStr) => {
    if (!dateStr) return true;
    
    // Safely parse dateStr to local YYYY-MM-DD
    const normalize = (val) => {
      if (!val) return "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      const d = new Date(val);
      if (isNaN(d.getTime())) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const r = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${r}`;
    };

    const recordDate = normalize(dateStr);
    if (!recordDate) return true;

    if (startDate) {
      const filterStart = normalize(startDate);
      if (recordDate < filterStart) return false;
    }
    if (endDate) {
      const filterEnd = normalize(endDate);
      if (recordDate > filterEnd) return false;
    }
    return true;
  }, [startDate, endDate]);

  // Helper: check client bounds
  const isClientAllowed = useCallback((clientName) => {
    if (selectedClient === "All") return true;
    return String(clientName).toLowerCase() === String(selectedClient).toLowerCase();
  }, [selectedClient]);

  // Unique clients list for dropdown
  const clientCompaniesList = useMemo(() => {
    const list = new Set();
    positionMatrix.forEach(row => {
      if (row.company_name) list.add(row.company_name);
    });
    placements.forEach(row => {
      if (row.company) list.add(row.company);
    });
    return Array.from(list).sort();
  }, [positionMatrix, placements]);

  // -------------------------------------------------------------
  // FILTERED DATA SETS
  // -------------------------------------------------------------

  const filteredPlacements = useMemo(() => {
    return placements.filter(row => {
      let uAllowed = isUserAllowed(row.user_id) || isUserAllowed(row.added_by_id) || isUserAllowed(row.entered_by);
      if (!allowedUserIds) {
        uAllowed = true;
      } else if (!uAllowed && row.recruiter_name) {
        const foundRec = teamMembers.find(t => t.name?.toLowerCase() === row.recruiter_name?.toLowerCase());
        if (foundRec && allowedUserIds.includes(Number(foundRec.userId))) {
          uAllowed = true;
        }
      }

      const dAllowed = isDateInBounds(row.doj);
      const cAllowed = isClientAllowed(row.company);
      return uAllowed && dAllowed && cAllowed;
    });
  }, [placements, isUserAllowed, isDateInBounds, isClientAllowed, allowedUserIds, teamMembers]);

  const filteredPositionMatrix = useMemo(() => {
    return positionMatrix.filter(row => {
      const uAllowed = isUserAllowed(row.user_id);
      const dAllowed = isDateInBounds(row.position_received_on);
      const cAllowed = isClientAllowed(row.company_name);
      return uAllowed && dAllowed && cAllowed;
    });
  }, [positionMatrix, isUserAllowed, isDateInBounds, isClientAllowed]);

  const filteredCandidates = useMemo(() => {
    const allowedReqIds = selectedClient === "All" 
      ? null 
      : new Set(
          positionMatrix
            .filter(row => String(row.company_name).toLowerCase() === selectedClient.toLowerCase())
            .map(row => row.req_id)
        );

    return candidates.filter(row => {
      const uAllowed = isUserAllowed(row.added_by_id);
      const dAllowed = isDateInBounds(row.uploaded_on);
      const cAllowed = allowedReqIds === null 
        ? true 
        : (row.company 
            ? isClientAllowed(row.company) 
            : (row.current_company 
                ? isClientAllowed(row.current_company) 
                : (row.req_id ? allowedReqIds.has(row.req_id) : false)));
      return uAllowed && dAllowed && cAllowed;
    });
  }, [candidates, positionMatrix, selectedClient, isUserAllowed, isDateInBounds, isClientAllowed]);

  const filteredInterviews = useMemo(() => {
    return interviews.filter(row => {
      const uAllowed = isUserAllowed(row.added_by_id);
      const dAllowed = isDateInBounds(row.interview_date);
      const cAllowed = isClientAllowed(row.company);
      return uAllowed && dAllowed && cAllowed;
    });
  }, [interviews, isUserAllowed, isDateInBounds, isClientAllowed]);

  // -------------------------------------------------------------
  // KPI CALCULATIONS
  // -------------------------------------------------------------

  // Helper to map and merge placements from tracker and position matrix
  const finalPlacements = useMemo(() => {
    let result = [];

    if (filteredPlacements && filteredPlacements.length > 0) {
      result = filteredPlacements.map(p => {
        const teamMember = teamMembers.find(t => String(t.userId) === String(p.user_id || p.added_by_id));
        let managerName = "Director";
        if (teamMember) {
          const mgr = teamMembers.find(t => String(t.userId) === String(teamMember.reportingManagerId));
          managerName = mgr ? mgr.name : "Director";
        }

        return {
          ...p,
          candidate_name: p.candidate_name || "Joined Candidate",
          company: p.company || "—",
          designation: p.designation || p.position || "—",
          doj: p.doj,
          ctc_offered: p.ctc_offered || p.offered_ctc || 0,
          amount: Number(p.amount) || 0,
          recruiter_name: p.recruiter_name || teamMember?.name || "Recruiter",
          managerName
        };
      });
    } else {
      // Fallback to joined positions from filteredPositionMatrix so modal table ALWAYS populates when revenue > 0
      filteredPositionMatrix.filter(j => Number(j.joined) > 0).forEach(j => {
        const teamMember = teamMembers.find(t => Number(t.userId) === Number(j.user_id));
        let managerName = "Director";
        if (teamMember) {
          const mgr = teamMembers.find(t => String(t.userId) === String(teamMember.reportingManagerId));
          managerName = mgr ? mgr.name : "Director";
        }

        const joinedCount = Number(j.joined) || 1;
        const openings = Number(j.no_of_openings) || 1;
        const totalRev = Number(j.company_revenue) || 0;
        const revPerJoined = openings > 0 ? (totalRev / openings) : 0;

        for (let i = 0; i < joinedCount; i++) {
          result.push({
            candidate_name: j.candidate_name || `Placed Candidate ${joinedCount > 1 ? i + 1 : ''}`.trim(),
            company: j.company_name || "—",
            designation: j.job_title || j.position || "—",
            position: j.job_title || j.position || "—",
            doj: j.position_received_on,
            ctc_offered: j.offered_ctc || j.max_ctc || 0,
            amount: revPerJoined,
            recruiter_name: j.recruiter_name || teamMember?.name || "Recruiter",
            managerName
          });
        }
      });
    }
    return result;
  }, [filteredPlacements, filteredPositionMatrix, teamMembers]);

  // -------------------------------------------------------------
  // KPI CALCULATIONS
  // -------------------------------------------------------------

  const kpis = useMemo(() => {
    // Placement revenue from position matrix of joined candidates
    const totalRevenue = finalPlacements.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    const activeOpenings = filteredPositionMatrix
      .filter(row => row.final_status === "Active")
      .reduce((sum, item) => sum + (Number(item.no_of_openings) || 0), 0);
    
    const totalSourced = filteredCandidates.length;

    // Total interviews done so far (number of interviews in the date range)
    const totalInterviews = filteredInterviews.length;
    
    // Placed Count
    const placedCount = finalPlacements.length;

    // Rejected Count
    const matrixRejected = filteredPositionMatrix.reduce((sum, item) => sum + (Number(item.rejected) || 0), 0);
    const candRejected = filteredCandidates.filter(c => c.status?.toLowerCase().includes("reject")).length;
    const rejectedCount = Math.max(matrixRejected, candRejected);

    // Dropping Count (No Show & Backout)
    const matrixDropping = filteredPositionMatrix.reduce((sum, item) => sum + (Number(item.no_show) || 0) + (Number(item.backout) || 0), 0);
    const candDropping = filteredCandidates.filter(c => {
      const s = c.status?.toLowerCase() || "";
      return s.includes("backout") || s.includes("no show") || s.includes("drop") || s.includes("hold");
    }).length;
    const droppingCount = Math.max(matrixDropping, candDropping);

    // Profiles shared (Sourced count from matrix)
    const profilesShared = filteredPositionMatrix.reduce((sum, item) => sum + (Number(item.profiles_shared) || 0), 0);
    
    const conversionRate = profilesShared > 0 ? Math.round((placedCount / profilesShared) * 100) : 0;
    const rejectionRate = profilesShared > 0 ? Math.round((rejectedCount / profilesShared) * 100) : 0;
    const droppingRate = profilesShared > 0 ? Math.round((droppingCount / profilesShared) * 100) : 0;

    return {
      totalRevenue,
      activeOpenings,
      totalSourced,
      totalInterviews,
      profilesShared,
      placedCount,
      conversionRate,
      rejectedCount,
      rejectionRate,
      droppingCount,
      droppingRate
    };
  }, [finalPlacements, filteredPositionMatrix, filteredCandidates, filteredInterviews]);

  // -------------------------------------------------------------
  // VISUAL ANALYTICS CHART DATA
  // -------------------------------------------------------------

  // 1. Revenue month-over-month trend
  const revenueTrendData = useMemo(() => {
    const monthsMap = {};
    finalPlacements.forEach(p => {
      if (!p.doj) return;
      const date = new Date(p.doj);
      if (isNaN(date)) return;
      const monthYear = date.toLocaleString("en-US", { month: "short", year: "2-digit" });
      monthsMap[monthYear] = (monthsMap[monthYear] || 0) + (Number(p.amount) || 0);
    });

    // Sort months
    const sortedKeys = Object.keys(monthsMap).sort((a, b) => {
      const d1 = new Date("01 " + a);
      const d2 = new Date("01 " + b);
      return d1 - d2;
    });

    return sortedKeys.map(key => ({
      month: key,
      Revenue: monthsMap[key]
    }));
  }, [finalPlacements]);

  // 2. Hiring funnel data
  const hiringFunnelData = useMemo(() => {
    let shared = 0;
    let feedback = 0;
    let screening = 0;
    let shortlisted = 0;
    let interviewed = 0;
    let selected = 0;
    let offered = 0;
    let joined = 0;

    filteredPositionMatrix.forEach(row => {
      shared += Number(row.profiles_shared) || 0;
      feedback += Number(row.feedback_pending) || 0;
      screening += Number(row.screening) || 0;
      shortlisted += Number(row.shortlisted) || 0;
      interviewed += (Number(row.interview_l1) || 0) + (Number(row.interview_l2) || 0) + (Number(row.face_to_face) || 0);
      selected += Number(row.selected) || 0;
      offered += Number(row.offered) || 0;
      joined += Number(row.joined) || 0;
    });

    return [
      { stage: "Profiles Shared", Count: shared, fill: "#818cf8" },
      { stage: "Screening/Pending", Count: feedback + screening, fill: "#fbbf24" },
      { stage: "Shortlisted", Count: shortlisted, fill: "#34d399" },
      { stage: "Interviewed", Count: interviewed, fill: "#60a5fa" },
      { stage: "Selected & Offered", Count: selected + offered, fill: "#a78bfa" },
      { stage: "Joined/Placed", Count: joined, fill: "#059669" }
    ];
  }, [filteredPositionMatrix]);

  // 3. Client revenue contribution (top 5)
  const clientRevenueData = useMemo(() => {
    const clientsMap = {};
    finalPlacements.forEach(p => {
      const name = p.company || "Unknown Client";
      clientsMap[name] = (clientsMap[name] || 0) + (Number(p.amount) || 0);
    });

    const list = Object.entries(clientsMap).map(([name, value]) => ({
      name,
      value
    })).sort((a, b) => b.value - a.value);

    const top5 = list.slice(0, 5);
    const others = list.slice(5).reduce((sum, item) => sum + item.value, 0);
    if (others > 0) {
      top5.push({ name: "Others", value: others });
    }
    return top5;
  }, [finalPlacements]);

  // 4. Top Recruiters leaderboard (top 5 by placements)
  const recruiterLeaderboardData = useMemo(() => {
    const recruitersMap = {};
    finalPlacements.forEach(p => {
      const name = p.recruiter_name || "Unassigned Recruiter";
      if (!recruitersMap[name]) {
        recruitersMap[name] = { name, Placements: 0, Revenue: 0 };
      }
      recruitersMap[name].Placements += 1;
      recruitersMap[name].Revenue += Number(p.amount) || 0;
    });

    return Object.values(recruitersMap)
      .sort((a, b) => b.Placements - a.Placements)
      .slice(0, 5);
  }, [finalPlacements]);

  // -------------------------------------------------------------
  // MATRIX TABLES CALCULATIONS
  // -------------------------------------------------------------

  // 1. Recruiter Productivity Matrix
  const recruiterProductivityMatrix = useMemo(() => {
    const map = {};
    
    // Add all users from usersList first to capture all active recruiters and members
    usersList.forEach(u => {
      map[u.id] = {
        recruiterId: u.id,
        name: u.name,
        designation: u.designation,
        managerName: "Director", // default
        sourced: 0,
        shared: 0,
        shortlisted: 0,
        interviewed: 0,
        placed: 0,
        revenue: 0
      };
    });

    // Populate manager names from team members
    teamMembers.forEach(m => {
      if (map[m.userId]) {
        const mgr = teamMembers.find(t => String(t.userId) === String(m.reportingManagerId));
        map[m.userId].managerName = mgr ? mgr.name : "Director";
      }
    });

    // Aggregate Candidate uploads (Sourced CVs)
    filteredCandidates.forEach(cand => {
      const rId = cand.added_by_id;
      if (map[rId]) {
        map[rId].sourced += 1;
      }
    });

    // Aggregate Job submissions & shortlisted from position matrix
    filteredPositionMatrix.forEach(job => {
      const rId = job.user_id;
      if (map[rId]) {
        map[rId].shared += Number(job.profiles_shared) || 0;
        map[rId].shortlisted += Number(job.shortlisted) || 0;
      }
    });

    // Aggregate Interviews from filteredInterviews
    filteredInterviews.forEach(interview => {
      const rId = interview.added_by_id;
      if (map[rId]) {
        map[rId].interviewed += 1;
      }
    });

    // Aggregate Placements & Revenue from finalPlacements
    finalPlacements.forEach(p => {
      let rId = p.user_id || p.added_by_id || p.entered_by;
      if (!rId && p.recruiter_name) {
        const found = usersList.find(u => u.name?.toLowerCase() === p.recruiter_name.toLowerCase());
        if (found) rId = found.id;
      }
      if (rId && map[rId]) {
        map[rId].placed += 1;
        map[rId].revenue += Number(p.amount) || 0;
      }
    });

    let result = Object.values(map);

    // Apply allowedUserIds filter if team selector is applied
    if (allowedUserIds) {
      result = result.filter(r => allowedUserIds.includes(Number(r.recruiterId)));
    }

    // Filter to show only recruiters OR anyone with activity (to include managers/leads with activity)
    result = result.filter(r => {
      const des = r.designation?.toLowerCase() || "";
      const isRecruiter = des.includes("recruiter") ||
                          des.includes("lead") ||
                          des.includes("hr") ||
                          des.includes("associate") ||
                          des.includes("talent");
      const hasActivity = r.sourced > 0 ||
                          r.shared > 0 ||
                          r.shortlisted > 0 ||
                          r.interviewed > 0 ||
                          r.placed > 0 ||
                          r.revenue > 0;
      return isRecruiter || hasActivity;
    });

    if (selectedClient !== "All") {
      result = result.filter(r => r.sourced > 0 || r.shared > 0 || r.shortlisted > 0 || r.interviewed > 0 || r.placed > 0 || r.revenue > 0);
    }

    // Apply search filter
    if (recruiterSearch) {
      const s = recruiterSearch.toLowerCase();
      result = result.filter(r => r.name.toLowerCase().includes(s));
    }

    return result.sort((a, b) => b.revenue - a.revenue);
  }, [usersList, teamMembers, filteredCandidates, filteredPositionMatrix, filteredInterviews, finalPlacements, recruiterSearch, selectedClient, allowedUserIds]);

  // 2. Manager & Team Matrix
  const managerTeamMatrix = useMemo(() => {
    let result = managers.map(mgr => {
      const teamUserIds = getTeamUserIds(mgr.userId);
      const allTeamIds = [Number(mgr.userId), ...teamUserIds];

      // Calculate team statistics from filtered dataset
      const teamSourced = filteredCandidates.filter(cand => 
        allTeamIds.includes(Number(cand.added_by_id))
      ).length;

      let teamShared = 0;
      let teamShortlisted = 0;

      filteredPositionMatrix.filter(job => 
        allTeamIds.includes(Number(job.user_id))
      ).forEach(job => {
        teamShared += Number(job.profiles_shared) || 0;
        teamShortlisted += Number(job.shortlisted) || 0;
      });

      let teamPlacements = 0;
      let teamRevenue = 0;
      finalPlacements.forEach(p => {
        let rId = p.user_id || p.added_by_id || p.entered_by;
        if (!rId && p.recruiter_name) {
          const found = usersList.find(u => u.name?.toLowerCase() === p.recruiter_name.toLowerCase());
          if (found) rId = found.id;
        }
        if (rId && allTeamIds.includes(Number(rId))) {
          teamPlacements += 1;
          teamRevenue += Number(p.amount) || 0;
        }
      });

      return {
        managerId: mgr.userId,
        name: mgr.name,
        designation: mgr.designation,
        teamSize: teamUserIds.length + 1, // team + manager
        sourced: teamSourced,
        shared: teamShared,
        shortlisted: teamShortlisted,
        placed: teamPlacements,
        revenue: teamRevenue
      };
    });

    if (selectedClient !== "All") {
      result = result.filter(m => m.sourced > 0 || m.shared > 0 || m.shortlisted > 0 || m.placed > 0 || m.revenue > 0);
    }

    // Apply manager search
    if (managerSearch) {
      const s = managerSearch.toLowerCase();
      result = result.filter(r => r.name.toLowerCase().includes(s));
    }

    if (allowedUserIds) {
      result = result.filter(m => allowedUserIds.includes(Number(m.managerId)));
    }

    return result.sort((a, b) => b.revenue - a.revenue);
  }, [managers, filteredCandidates, filteredPositionMatrix, finalPlacements, usersList, managerSearch, getTeamUserIds, selectedClient, allowedUserIds]);

  const clientPerformanceMatrix = useMemo(() => {
    const clientsMap = {};

    filteredPositionMatrix.forEach(row => {
      const name = row.company_name || "Unknown Client";
      if (!clientsMap[name]) {
        clientsMap[name] = { name, openJobs: 0, shared: 0, placed: 0, revenue: 0 };
      }
      if (row.final_status === "Active") {
        clientsMap[name].openJobs += Number(row.no_of_openings) || 0;
      }
      clientsMap[name].shared += Number(row.profiles_shared) || 0;
    });

    finalPlacements.forEach(p => {
      const name = p.company || "Unknown Client";
      if (!clientsMap[name]) {
        clientsMap[name] = { name, openJobs: 0, shared: 0, placed: 0, revenue: 0 };
      }
      clientsMap[name].placed += 1;
      clientsMap[name].revenue += Number(p.amount) || 0;
    });

    let result = Object.values(clientsMap);

    if (clientSearch) {
      const s = clientSearch.toLowerCase();
      result = result.filter(c => c.name.toLowerCase().includes(s));
    }

    return result.sort((a, b) => b.revenue - a.revenue);
  }, [filteredPositionMatrix, finalPlacements, clientSearch]);

  // 4. Placements list report
  const placementsListReport = useMemo(() => {
    let result = finalPlacements;

    if (placementSearch) {
      const s = placementSearch.toLowerCase();
      result = result.filter(p => 
        p.candidate_name?.toLowerCase().includes(s) || 
        p.company?.toLowerCase().includes(s) || 
        p.recruiter_name?.toLowerCase().includes(s)
      );
    }

    return result;
  }, [finalPlacements, placementSearch]);

  // -------------------------------------------------------------
  // EXCEL EXPORTS HELPER
  // -------------------------------------------------------------

  const exportTableToExcel = (tableType) => {
    let dataToExport = [];
    let fileName = "";

    if (tableType === "recruiter") {
      dataToExport = recruiterProductivityMatrix.map(r => ({
        "Recruiter Name": r.name,
        "Designation": r.designation,
        "Reporting Manager": r.managerName,
        "CVs Sourced": r.sourced,
        "Profiles Shared": r.shared,
        "Shortlisted": r.shortlisted,
        "Interviews Conducted": r.interviewed,
        "Candidates Placed": r.placed,
        "Revenue Generated (INR)": r.revenue
      }));
      fileName = "Recruiter_Productivity_Report";
    } else if (tableType === "manager") {
      dataToExport = managerTeamMatrix.map(m => ({
        "Manager/TL Name": m.name,
        "Designation": m.designation,
        "Team Size": m.teamSize,
        "Team CVs Sourced": m.sourced,
        "Team Profiles Shared": m.shared,
        "Team Shortlisted": m.shortlisted,
        "Team Placements": m.placed,
        "Team Revenue Generated (INR)": m.revenue
      }));
      fileName = "Manager_and_Team_Performance_Report";
    } else if (tableType === "client") {
      dataToExport = clientPerformanceMatrix.map(c => ({
        "Client Company Name": c.name,
        "Active Openings": c.openJobs,
        "Profiles Shared": c.shared,
        "Placed Candidates": c.placed,
        "Revenue Generated (INR)": c.revenue
      }));
      fileName = "Client_Performance_Report";
    } else if (tableType === "placement") {
      dataToExport = placementsListReport.map(p => ({
        "Candidate Name": p.candidate_name,
        "Designation": p.designation,
        "Client Company": p.company,
        "Date of Joining": p.doj,
        "Recruiter": p.recruiter_name,
        "Reporting Manager": p.managerName,
        "Offered CTC (INR)": p.ctc_offered,
        "Revenue Earned (INR)": p.amount
      }));
      fileName = "Placements_Detailed_Report";
    }

    if (dataToExport.length === 0) {
      toast.warning("No records found to export");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${fileName}_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Excel spreadsheet exported successfully!");
  };

  // -------------------------------------------------------------
  // DRILL DOWN HANDLERS
  // -------------------------------------------------------------

  const handleRecruiterClick = (recruiterId) => {
    // Find recruiter metadata
    const recObj = recruiterProductivityMatrix.find(r => r.recruiterId === recruiterId);
    if (!recObj) return;

    // Filter jobs assigned to them from filteredPositionMatrix
    const recruiterJobs = filteredPositionMatrix.filter(j => Number(j.user_id) === Number(recruiterId));
    
    let recruiterPlacements = [];

    // Source 1: Joined candidates from filteredCandidates array
    filteredCandidates.filter(c => 
      Number(c.added_by_id) === Number(recruiterId) && 
      (c.status === "Joined" || c.status?.toLowerCase().includes("joined"))
    ).forEach(c => {
      const matchJob = filteredPositionMatrix.find(j => String(j.req_id) === String(c.req_id));
      recruiterPlacements.push({
        req_id: c.req_id,
        candidate_name: `${c.f_name || ""} ${c.l_name || ""}`.trim() || "Joined Candidate",
        position: c.designation || matchJob?.position || matchJob?.job_title || "—",
        company: c.current_company || matchJob?.company_name || "—",
        doj: c.uploaded_on || c.updated_on || matchJob?.position_received_on,
        ctc_offered: c.expected_ctc || c.current_ctc || matchJob?.budget || 0,
        amount: matchJob ? (Number(matchJob.company_revenue) / (Number(matchJob.no_of_openings) || 1)) : 0,
        recruiter_name: recObj.name
      });
    });

    // Source 2: Placement tracker entries from filteredPlacements
    filteredPlacements.filter(p => 
      Number(p.user_id) === Number(recruiterId) || 
      Number(p.added_by_id) === Number(recruiterId) || 
      Number(p.entered_by) === Number(recruiterId)
    ).forEach(p => {
      recruiterPlacements.push({
        req_id: p.req_id,
        candidate_name: p.candidate_name || "Joined Candidate",
        position: p.position || p.designation || "—",
        company: p.company || "—",
        doj: p.doj,
        ctc_offered: p.ctc_offered || 0,
        amount: Number(p.amount) || 0,
        recruiter_name: p.recruiter_name || recObj.name
      });
    });

    // Source 3: Fallback to recruiterJobs in filteredPositionMatrix where joined > 0
    recruiterJobs.filter(j => Number(j.joined) > 0).forEach(j => {
      const joinedCount = Number(j.joined) || 1;
      const openings = Number(j.no_of_openings) || 1;
      const totalRev = Number(j.company_revenue) || 0;
      const revPerJoined = openings > 0 ? (totalRev / openings) : 0;

      const existingCount = recruiterPlacements.filter(rp => 
        (rp.req_id && String(rp.req_id) === String(j.req_id)) ||
        (rp.position === (j.job_title || j.position) && rp.company === j.company_name)
      ).length;

      const needed = Math.max(0, joinedCount - existingCount);

      for (let i = 0; i < needed; i++) {
        recruiterPlacements.push({
          req_id: j.req_id,
          candidate_name: j.candidate_name || `Joined Candidate ${joinedCount > 1 ? i + 1 : ''}`.trim(),
          position: j.job_title || j.position || "—",
          company: j.company_name || "—",
          doj: j.position_received_on,
          ctc_offered: j.offered_ctc || j.max_ctc || j.budget || 0,
          amount: revPerJoined,
          recruiter_name: recObj.name
        });
      }
    });

    // Filter candidates for top 10 list from filteredCandidates
    const recruiterCandidates = filteredCandidates.filter(c => Number(c.added_by_id) === Number(recruiterId)).slice(0, 10);

    setSelectedRecruiterDetails({
      recruiter: recObj,
      jobs: recruiterJobs,
      placements: recruiterPlacements,
      candidates: recruiterCandidates
    });
  };

  const handleManagerClick = (managerId) => {
    const mgrObj = managerTeamMatrix.find(m => m.managerId === managerId);
    if (!mgrObj) return;

    // Find direct reports
    const reports = teamMembers.filter(t => String(t.reportingManagerId) === String(managerId));
    const teamIds = [Number(managerId), ...getTeamUserIds(managerId)];

    let teamPlacementsList = [];

    // Source 1: Joined candidates from filteredCandidates array
    filteredCandidates.filter(c => 
      teamIds.includes(Number(c.added_by_id)) && 
      (c.status === "Joined" || c.status?.toLowerCase().includes("joined"))
    ).forEach(c => {
      const matchJob = filteredPositionMatrix.find(j => String(j.req_id) === String(c.req_id));
      const teamMember = teamMembers.find(t => Number(t.userId) === Number(c.added_by_id));
      teamPlacementsList.push({
        req_id: c.req_id,
        candidate_name: `${c.f_name || ""} ${c.l_name || ""}`.trim() || "Joined Candidate",
        position: c.designation || matchJob?.position || matchJob?.job_title || "—",
        company: c.current_company || matchJob?.company_name || "—",
        doj: c.uploaded_on || c.updated_on || matchJob?.position_received_on,
        ctc_offered: c.expected_ctc || c.current_ctc || matchJob?.budget || 0,
        amount: matchJob ? (Number(matchJob.company_revenue) / (Number(matchJob.no_of_openings) || 1)) : 0,
        recruiter_name: teamMember?.name || "Recruiter"
      });
    });

    // Source 2: Placement tracker entries from filteredPlacements
    filteredPlacements.filter(p => 
      teamIds.includes(Number(p.user_id)) || 
      teamIds.includes(Number(p.added_by_id)) || 
      teamIds.includes(Number(p.entered_by))
    ).forEach(p => {
      teamPlacementsList.push({
        req_id: p.req_id,
        candidate_name: p.candidate_name || "Joined Candidate",
        company: p.company || "—",
        position: p.position || p.designation || "—",
        doj: p.doj,
        ctc_offered: p.ctc_offered || 0,
        amount: Number(p.amount) || 0,
        recruiter_name: p.recruiter_name || "Recruiter"
      });
    });

    // Source 3: Fallback to filteredPositionMatrix joined counts
    filteredPositionMatrix.filter(j => teamIds.includes(Number(j.user_id)) && Number(j.joined) > 0).forEach(j => {
      const teamMember = teamMembers.find(t => Number(t.userId) === Number(j.user_id));
      const recruiterName = j.recruiter_name || j.associate_name || teamMember?.name || "Recruiter";
      const joinedCount = Number(j.joined) || 1;
      const openings = Number(j.no_of_openings) || 1;
      const totalRev = Number(j.company_revenue) || 0;
      const revPerJoined = openings > 0 ? (totalRev / openings) : 0;

      const existingCount = teamPlacementsList.filter(tp => 
        (tp.req_id && String(tp.req_id) === String(j.req_id)) ||
        (tp.position === (j.job_title || j.position) && tp.company === j.company_name)
      ).length;

      const needed = Math.max(0, joinedCount - existingCount);

      for (let i = 0; i < needed; i++) {
        teamPlacementsList.push({
          req_id: j.req_id,
          candidate_name: j.candidate_name || `Joined Candidate ${joinedCount > 1 ? i + 1 : ''}`.trim(),
          company: j.company_name || "—",
          position: j.job_title || j.position || "—",
          doj: j.position_received_on,
          ctc_offered: j.offered_ctc || j.max_ctc || j.budget || 0,
          amount: revPerJoined,
          recruiter_name: recruiterName
        });
      }
    });

    setSelectedManagerDetails({
      manager: mgrObj,
      reports,
      placements: teamPlacementsList
    });
  };

  // -------------------------------------------------------------
  // FORMAT CURRENCY
  // -------------------------------------------------------------
  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm font-bold text-gray-400">Loading company-wide reports and analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 font-sans text-gray-800 dark:text-slate-100 animate-fadeIn">
      
      {/* ================================================= */}
      {/* HEADER SECTION WITH FILTERS */}
      {/* ================================================= */}
      <div className="bg-white dark:bg-[#111827]/95 border border-gray-100 dark:border-slate-800 p-6 rounded-[32px] shadow-sm">
        {/* Dynamic Controls */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end w-full">
          {/* Manager / Team Filter */}
          <div>
            <label className="text-[10px] font-extrabold text-gray-400 dark:text-slate-400 uppercase tracking-wider block mb-2">Team Selector</label>
            <select
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-2xl bg-[#f4f6fa]/60 dark:bg-slate-800/60 text-gray-750 dark:text-slate-200 border border-transparent dark:border-slate-700/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-bold transition cursor-pointer"
            >
              <option value="All" className="dark:bg-slate-900 dark:text-slate-200">All Company Teams</option>
              {dropdownManagers.map(mgr => (
                <option key={mgr.userId} value={mgr.userId} className="dark:bg-slate-900 dark:text-slate-200">
                  {mgr.name}'s Team ({mgr.designation})
                </option>
              ))}
            </select>
          </div>

          {/* Client Filter */}
          <div>
            <label className="text-[10px] font-extrabold text-gray-400 dark:text-slate-400 uppercase tracking-wider block mb-2">Client Company</label>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-2xl bg-[#f4f6fa]/60 dark:bg-slate-800/60 text-gray-750 dark:text-slate-200 border border-transparent dark:border-slate-700/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-bold transition cursor-pointer"
            >
              <option value="All" className="dark:bg-slate-900 dark:text-slate-200">All Client Companies</option>
              {clientCompaniesList.map(name => (
                <option key={name} value={name} className="dark:bg-slate-900 dark:text-slate-200">{name}</option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="text-[10px] font-extrabold text-gray-400 dark:text-slate-400 uppercase tracking-wider block mb-2">Date From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-2xl bg-[#f4f6fa]/60 dark:bg-slate-800/60 text-gray-750 dark:text-slate-200 border border-transparent dark:border-slate-700/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-bold transition"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="text-[10px] font-extrabold text-gray-400 dark:text-slate-400 uppercase tracking-wider block mb-2">Date To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-2xl bg-[#f4f6fa]/60 dark:bg-slate-800/60 text-gray-750 dark:text-slate-200 border border-transparent dark:border-slate-700/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-bold transition"
            />
          </div>
        </div>
      </div>

      {/* ================================================= */}
      {/* METRIC SUMMARY CARDS */}
      {/* ================================================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        
        {/* Card 1: Revenue */}
        <div 
          onClick={() => setActiveKpiModal("revenue")}
          className="cursor-pointer bg-gradient-to-br from-emerald-50/50 to-teal-50/50 dark:from-emerald-950/20 dark:to-teal-950/20 border border-emerald-100 dark:border-emerald-900/40 rounded-3xl p-5 shadow-sm hover:shadow-md hover:scale-[1.02] transition duration-200"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Closure Revenue</span>
            <span className="p-2 bg-emerald-500 text-white rounded-2xl shadow-sm shadow-emerald-100">
              <FiDollarSign className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-xl font-black text-gray-900 dark:text-slate-100 leading-none">{formatCurrency(kpis.totalRevenue)}</h3>
            <p className="text-[10px] text-gray-400 dark:text-slate-400 font-bold mt-2 uppercase">Earned from closures</p>
          </div>
        </div>

        {/* Card 2: Active Openings */}
        <div 
          onClick={() => setActiveKpiModal("openings")}
          className="cursor-pointer bg-gradient-to-br from-indigo-50/50 to-purple-50/50 dark:from-indigo-950/20 dark:to-purple-950/20 border border-indigo-100 dark:border-indigo-900/40 rounded-3xl p-5 shadow-sm hover:shadow-md hover:scale-[1.02] transition duration-200"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">Active Job Openings</span>
            <span className="p-2 bg-indigo-500 text-white rounded-2xl shadow-sm shadow-indigo-100">
              <FiBriefcase className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-xl font-black text-gray-900 dark:text-slate-100 leading-none">{kpis.activeOpenings} Positions</h3>
            <p className="text-[10px] text-gray-400 dark:text-slate-400 font-bold mt-2 uppercase">Currently active in funnel</p>
          </div>
        </div>

        {/* Card 3: CV Sourced */}
        <div 
          onClick={() => setActiveKpiModal("sourced")}
          className="cursor-pointer bg-gradient-to-br from-sky-50/50 to-blue-50/50 dark:from-sky-950/20 dark:to-blue-950/20 border border-sky-100 dark:border-sky-900/40 rounded-3xl p-5 shadow-sm hover:shadow-md hover:scale-[1.02] transition duration-200"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-sky-600 dark:text-sky-400 uppercase tracking-wider">CVs Sourced</span>
            <span className="p-2 bg-sky-500 text-white rounded-2xl shadow-sm shadow-sky-100">
              <FiUsers className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-xl font-black text-gray-900 dark:text-slate-100 leading-none">{kpis.totalSourced} Resumes</h3>
            <p className="text-[10px] text-gray-400 dark:text-slate-400 font-bold mt-2 uppercase">Total candidates uploaded</p>
          </div>
        </div>

        {/* Card 4: Interviews */}
        <div 
          onClick={() => setActiveKpiModal("interviews")}
          className="cursor-pointer bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-100 dark:border-amber-900/40 rounded-3xl p-5 shadow-sm hover:shadow-md hover:scale-[1.02] transition duration-200"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Interviews Tracked</span>
            <span className="p-2 bg-amber-500 text-white rounded-2xl shadow-sm shadow-amber-100">
              <FiCalendar className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-xl font-black text-gray-900 dark:text-slate-100 leading-none">{kpis.totalInterviews} Interviews</h3>
            <p className="text-[10px] text-gray-400 dark:text-slate-400 font-bold mt-2 uppercase">candidates interviewed</p>
          </div>
        </div>

        {/* Card 5: Conversion Rate */}
        <div 
          onClick={() => setActiveKpiModal("conversion")}
          className="cursor-pointer bg-gradient-to-br from-violet-50/50 to-pink-50/50 dark:from-violet-950/20 dark:to-pink-950/20 border border-violet-100 dark:border-violet-900/40 rounded-3xl p-5 shadow-sm hover:shadow-md hover:scale-[1.02] transition duration-200"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-violet-600 dark:text-violet-400 uppercase tracking-wider">Conversion Ratio</span>
            <span className="p-2 bg-violet-500 text-white rounded-2xl shadow-sm shadow-violet-100">
              <FiCheckCircle className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-4">
            <h3 className="text-xl font-black text-gray-900 dark:text-slate-100 leading-none">{kpis.conversionRate}%</h3>
            <p className="text-[10px] text-gray-400 dark:text-slate-400 font-bold mt-2 uppercase">CV Share to Joining conversion</p>
          </div>
        </div>

      </div>

      {/* ================================================= */}
      {/* DETAILED REPORT TABLES AND MATRICES WITH TABS */}
      {/* ================================================= */}
      <div className="bg-white dark:bg-[#111827]/95 border border-gray-100 dark:border-slate-800 rounded-[32px] shadow-sm overflow-hidden">
        
        {/* Navigation Tabs */}
        <div className="flex flex-wrap border-b border-gray-100 dark:border-slate-800 bg-[#fbfbfe] dark:bg-slate-900/40 px-6 py-2.5">
          <div className="flex flex-wrap gap-2">
            
            <button
              onClick={() => setActiveTab("recruiter")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black tracking-wide transition ${
                activeTab === "recruiter"
                  ? "bg-indigo-600 text-white shadow-lg dark:shadow-none"
                  : "text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800/50"
              }`}
            >
              <FiUsers className="w-4 h-4" />
              Recruiter Productivity
            </button>

            <button
              onClick={() => setActiveTab("manager")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black tracking-wide transition ${
                activeTab === "manager"
                  ? "bg-indigo-600 text-white shadow-lg dark:shadow-none"
                  : "text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800/50"
              }`}
            >
              <FiGrid className="w-4 h-4" />
              Manager & Team Performance
            </button>

            <button
              onClick={() => setActiveTab("client")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black tracking-wide transition ${
                activeTab === "client"
                  ? "bg-indigo-600 text-white shadow-lg dark:shadow-none"
                  : "text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800/50"
              }`}
            >
              <FiBriefcase className="w-4 h-4" />
              Client Analytics
            </button>

            <button
              onClick={() => setActiveTab("placement")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black tracking-wide transition ${
                activeTab === "placement"
                  ? "bg-indigo-600 text-white shadow-lg dark:shadow-none"
                  : "text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800/50"
              }`}
            >
              <FiList className="w-4 h-4" />
              Closure List
            </button>

          </div>
        </div>

        {/* Tab Content Header (Search and Export Controls) */}
        <div className="p-6 border-b border-gray-50 dark:border-slate-800/60 flex flex-col md:flex-row md:items-center justify-between gap-4">
          
          {/* Table search bar */}
          <div className="relative max-w-sm w-full">
            <span className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none text-gray-450 dark:text-slate-400">
              <FiSearch className="w-4 h-4" />
            </span>
            <input
              type="text"
              placeholder={
                activeTab === "recruiter"
                  ? "Search recruiter name..."
                  : activeTab === "manager"
                  ? "Search manager/TL name..."
                  : activeTab === "client"
                  ? "Search client company..."
                  : "Search candidate, company or recruiter..."
              }
              value={
                activeTab === "recruiter"
                  ? recruiterSearch
                  : activeTab === "manager"
                  ? managerSearch
                  : activeTab === "client"
                  ? clientSearch
                  : placementSearch
              }
              onChange={(e) => {
                const val = e.target.value;
                if (activeTab === "recruiter") setRecruiterSearch(val);
                else if (activeTab === "manager") setManagerSearch(val);
                else if (activeTab === "client") setClientSearch(val);
                else setPlacementSearch(val);
              }}
              className="w-full pl-10 pr-4 py-2.5 bg-[#f4f6fa]/60 dark:bg-slate-800/60 border border-transparent dark:border-slate-700 rounded-2xl text-xs focus:outline-none focus:ring-4 focus:ring-indigo-100/50 focus:bg-white dark:focus:bg-slate-900 focus:border-indigo-400 dark:focus:border-indigo-500 transition placeholder-gray-400 dark:placeholder-slate-500 text-gray-700 dark:text-slate-200 font-bold"
            />
          </div>

          {/* Export Control */}
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-bold text-gray-400 dark:text-slate-400 bg-gray-50 dark:bg-slate-900 px-2.5 py-1.5 rounded-lg border border-gray-100 dark:border-slate-800">
              {activeTab === "recruiter"
                ? `${recruiterProductivityMatrix.length} Recruiters`
                : activeTab === "manager"
                ? `${managerTeamMatrix.length} Manager Teams`
                : activeTab === "client"
                ? `${clientPerformanceMatrix.length} Clients`
                : `${placementsListReport.length} Closures`}
            </span>
            <button
              onClick={() => exportTableToExcel(activeTab)}
              className="px-4 py-2.5 bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100/80 dark:hover:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/50 rounded-2xl text-xs font-black transition duration-150 flex items-center gap-1.5 cursor-pointer"
            >
              <FiDownload className="w-3.5 h-3.5" />
              Spreadsheet Export
            </button>
          </div>

        </div>

        {/* Tab Tables content */}
        <div className="p-6">
          <div className="overflow-x-auto border border-gray-50 dark:border-slate-800 rounded-2xl no-scrollbar">
            
            {/* 1. Recruiter Productivity Matrix Table */}
            {activeTab === "recruiter" && (
              <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-slate-400 min-w-[1000px]">
                <thead>
                  <tr className="bg-gray-50/50 dark:bg-slate-900/20 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-widest font-black">
                    <th className="px-6 py-4">Recruiter</th>
                    <th className="px-4 py-4">Designation</th>
                    <th className="px-4 py-4">Reports To</th>
                    <th className="px-4 py-4 text-center">CVs Sourced</th>
                    <th className="px-4 py-4 text-center">Shared Profiles</th>
                    <th className="px-4 py-4 text-center">Shortlisted</th>
                    <th className="px-4 py-4 text-center">Interviews</th>
                    <th className="px-4 py-4 text-center">Closures</th>
                    <th className="px-6 py-4 text-right">Revenue Generated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50/80 dark:divide-slate-800/40">
                  {recruiterProductivityMatrix.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-10 text-gray-450 dark:text-slate-500 italic">No matching recruiter records found</td>
                    </tr>
                  ) : (
                    recruiterProductivityMatrix.map((r, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/30 dark:hover:bg-slate-800/20 transition">
                        <td className="px-6 py-3.5 whitespace-nowrap">
                          <button
                            onClick={() => handleRecruiterClick(r.recruiterId)}
                            className="text-gray-900 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline text-xs font-black text-left focus:outline-none"
                          >
                            {r.name}
                          </button>
                        </td>
                        <td className="px-4 py-3.5 text-gray-455 dark:text-slate-400 whitespace-nowrap font-semibold">{r.designation}</td>
                        <td className="px-4 py-3.5 text-indigo-650/80 dark:text-indigo-400 whitespace-nowrap font-bold">{r.managerName}</td>
                        <td className="px-4 py-3.5 text-center text-gray-700 dark:text-slate-300 whitespace-nowrap">{r.sourced}</td>
                        <td className="px-4 py-3.5 text-center text-gray-700 dark:text-slate-300 whitespace-nowrap">{r.shared}</td>
                        <td className="px-4 py-3.5 text-center text-gray-700 dark:text-slate-300 whitespace-nowrap">{r.shortlisted}</td>
                        <td className="px-4 py-3.5 text-center text-gray-700 dark:text-slate-300 whitespace-nowrap">{r.interviewed}</td>
                        <td className="px-4 py-3.5 text-center whitespace-nowrap">
                          <span className="px-2 py-0.5 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 rounded-md">{r.placed} Placed</span>
                        </td>
                        <td className="px-6 py-3.5 text-right text-gray-900 dark:text-slate-100 whitespace-nowrap font-black">
                          {formatCurrency(r.revenue)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {/* 2. Manager and Team Performance Table */}
            {activeTab === "manager" && (
              <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-slate-400 min-w-[1000px]">
                <thead>
                  <tr className="bg-gray-50/50 dark:bg-slate-900/20 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-widest font-black">
                    <th className="px-6 py-4">Manager / Team Lead</th>
                    <th className="px-4 py-4">Designation</th>
                    <th className="px-4 py-4 text-center">Team Headcount</th>
                    <th className="px-4 py-4 text-center">CVs Sourced</th>
                    <th className="px-4 py-4 text-center">Profiles Shared</th>
                    <th className="px-4 py-4 text-center">Shortlisted</th>
                    <th className="px-4 py-4 text-center">Closures</th>
                    <th className="px-6 py-4 text-right">Team Revenue Generated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50/80 dark:divide-slate-800/40">
                  {managerTeamMatrix.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-10 text-gray-450 dark:text-slate-500 italic">No manager or team records registered in organizational hierarchy</td>
                    </tr>
                  ) : (
                    managerTeamMatrix.map((m, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/30 dark:hover:bg-slate-800/20 transition">
                        <td className="px-6 py-3.5 whitespace-nowrap">
                          <button
                            onClick={() => handleManagerClick(m.managerId)}
                            className="text-gray-900 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline text-xs font-black text-left focus:outline-none"
                          >
                            {m.name}
                          </button>
                        </td>
                        <td className="px-4 py-3.5 text-gray-455 dark:text-slate-400 whitespace-nowrap font-semibold">{m.designation}</td>
                        <td className="px-4 py-3.5 text-center text-gray-700 dark:text-slate-300 whitespace-nowrap">{m.teamSize} reports</td>
                        <td className="px-4 py-3.5 text-center text-gray-700 dark:text-slate-300 whitespace-nowrap">{m.sourced}</td>
                        <td className="px-4 py-3.5 text-center text-gray-700 dark:text-slate-300 whitespace-nowrap">{m.shared}</td>
                        <td className="px-4 py-3.5 text-center text-gray-700 dark:text-slate-300 whitespace-nowrap">{m.shortlisted}</td>
                        <td className="px-4 py-3.5 text-center whitespace-nowrap">
                          <span className="px-2.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-400 rounded-md font-bold">{m.placed} Joined</span>
                        </td>
                        <td className="px-6 py-3.5 text-right text-gray-900 dark:text-slate-100 whitespace-nowrap font-black">
                          {formatCurrency(m.revenue)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {/* 3. Client Company Performance Table */}
            {activeTab === "client" && (
              <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-slate-400 min-w-[800px]">
                <thead>
                  <tr className="bg-gray-50/50 dark:bg-slate-900/20 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-widest font-black">
                    <th className="px-6 py-4">Client Company</th>
                    <th className="px-4 py-4 text-center">Active Position Budget</th>
                    <th className="px-4 py-4 text-center">Profiles Shared</th>
                    <th className="px-4 py-4 text-center">Closures</th>
                    <th className="px-6 py-4 text-right">Revenue Contributed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50/80 dark:divide-slate-800/40">
                  {clientPerformanceMatrix.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-10 text-gray-450 dark:text-slate-500 italic">No client records located</td>
                    </tr>
                  ) : (
                    clientPerformanceMatrix.map((c, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/30 dark:hover:bg-slate-800/20 transition">
                        <td className="px-6 py-3.5 text-gray-900 dark:text-slate-200 whitespace-nowrap font-black">{c.name}</td>
                        <td className="px-4 py-3.5 text-center text-indigo-655 dark:text-indigo-400 font-bold whitespace-nowrap">{c.openJobs} Openings</td>
                        <td className="px-4 py-3.5 text-center text-gray-700 dark:text-slate-300 whitespace-nowrap">{c.shared} shared</td>
                        <td className="px-4 py-3.5 text-center text-green-700 dark:text-green-400 whitespace-nowrap">
                          <span className="px-2 py-0.5 bg-green-50 dark:bg-green-950/30 rounded-md font-semibold">{c.placed} Placed</span>
                        </td>
                        <td className="px-6 py-3.5 text-right text-gray-900 dark:text-slate-100 whitespace-nowrap font-black">
                          {formatCurrency(c.revenue)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {/* 4. Closure List Details Table */}
            {activeTab === "placement" && (
              <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-slate-400 min-w-[1200px]">
                <thead>
                  <tr className="bg-gray-50/50 dark:bg-slate-900/20 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-widest font-black">
                    <th className="px-6 py-4">Candidate</th>
                    <th className="px-4 py-4">Designation</th>
                    <th className="px-4 py-4">Client Company</th>
                    <th className="px-4 py-4">Date of Joining</th>
                    <th className="px-4 py-4">Recruiter</th>
                    <th className="px-4 py-4">Team Manager</th>
                    <th className="px-4 py-4 text-center">Offered CTC</th>
                    <th className="px-6 py-4 text-right">Revenue Generated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50/80 dark:divide-slate-800/40">
                  {placementsListReport.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-10 text-gray-450 dark:text-slate-500 italic">No closure records registered inside database</td>
                    </tr>
                  ) : (
                    placementsListReport.map((p, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/30 dark:hover:bg-slate-800/20 transition">
                        <td className="px-6 py-3.5 text-gray-900 dark:text-slate-200 whitespace-nowrap font-black">{p.candidate_name}</td>
                        <td className="px-4 py-3.5 text-gray-450 dark:text-slate-400 whitespace-nowrap font-semibold">{p.designation || p.position}</td>
                        <td className="px-4 py-3.5 text-gray-800 dark:text-slate-300 whitespace-nowrap font-bold">{p.company}</td>
                        <td className="px-4 py-3.5 text-gray-500 dark:text-slate-450 whitespace-nowrap">{new Date(p.doj).toLocaleDateString("en-GB")}</td>
                        <td className="px-4 py-3.5 text-gray-700 dark:text-slate-300 whitespace-nowrap font-bold">{p.recruiter_name}</td>
                        <td className="px-4 py-3.5 text-indigo-650/80 dark:text-indigo-400 whitespace-nowrap font-bold">{p.managerName}</td>
                        <td className="px-4 py-3.5 text-center text-gray-700 dark:text-slate-300 whitespace-nowrap font-bold">{formatCurrency(p.ctc_offered)}</td>
                        <td className="px-6 py-3.5 text-right text-gray-900 dark:text-slate-100 whitespace-nowrap font-black">{formatCurrency(p.amount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}

          </div>
        </div>

      </div>

      {/* ================================================= */}
      {/* ANALYTICAL CHARTS GRIDS */}
      {/* ================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Monthly Revenue Trend Line/Area Chart */}
        <div className="bg-white dark:bg-[#111827]/95 border border-gray-100 dark:border-slate-800 p-6 rounded-[32px] shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-gray-950 dark:text-slate-200 uppercase tracking-wider">Monthly Revenue Trend</h3>
            <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2.5 py-1 rounded-xl">INR Amount</span>
          </div>
          <div className="h-64 w-full">
            {revenueTrendData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-xs text-gray-400">No placement records found inside this timeframe</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueTrendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                  <XAxis dataKey="month" stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(val) => `₹${val/1000}k`} />
                  <Tooltip formatter={(value) => [formatCurrency(value), "Revenue"]} contentStyle={{ borderRadius: "16px", border: "1px solid #f3f4f6", fontSize: "12px", fontFamily: "sans-serif" }} />
                  <Area type="monotone" dataKey="Revenue" stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#colorRev)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Hiring pipeline Funnel Diagnostic */}
        <div className="bg-white dark:bg-[#111827]/95 border border-gray-100 dark:border-slate-800 p-6 rounded-[32px] shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-gray-955 dark:text-slate-200 uppercase tracking-wider">Hiring Funnel Status</h3>
            <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2.5 py-1 rounded-xl">Candidate stage metrics</span>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hiringFunnelData} layout="vertical" barSize={12}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                <XAxis type="number" stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis dataKey="stage" type="category" stroke="#4b5563" fontSize={10} tickLine={false} axisLine={false} width={100} />
                <Tooltip formatter={(value) => [value, "Candidates"]} contentStyle={{ borderRadius: "16px", border: "1px solid #f3f4f6", fontSize: "12px" }} />
                <Bar dataKey="Count" radius={[0, 4, 4, 0]}>
                  {hiringFunnelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Client Revenue Contribution Donut */}
        <div className="bg-white dark:bg-[#111827]/95 border border-gray-100 dark:border-slate-800 p-6 rounded-[32px] shadow-sm space-y-4">
          <h3 className="text-sm font-black text-gray-955 dark:text-slate-200 uppercase tracking-wider">Top Clients (Revenue share)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div className="h-56">
              {clientRevenueData.length === 0 ? (
                <div className="flex items-center justify-center h-full text-xs text-gray-400">No client placements</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={clientRevenueData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {clientRevenueData.map((entry, index) => {
                        const colors = ["#4f46e5", "#10b981", "#fbbf24", "#60a5fa", "#ec4899", "#9ca3af"];
                        return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                      })}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="space-y-2">
              {clientRevenueData.map((client, index) => {
                const colors = ["bg-[#4f46e5]", "bg-[#10b981]", "bg-[#fbbf24]", "bg-[#60a5fa]", "bg-[#ec4899]", "bg-[#9ca3af]"];
                const total = clientRevenueData.reduce((sum, item) => sum + item.value, 0);
                const percent = total > 0 ? Math.round((client.value / total) * 100) : 0;
                return (
                  <div key={index} className="flex items-center justify-between text-xs animate-fadeIn">
                    <div className="flex items-center gap-2 truncate">
                      <span className={`w-2.5 h-2.5 rounded-full ${colors[index % colors.length]}`}></span>
                      <span className="font-bold text-gray-600 dark:text-slate-400 truncate">{client.name}</span>
                    </div>
                    <span className="font-black text-gray-800 dark:text-slate-200">{percent}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recruiter Placements Leaderboard */}
        <div className="bg-white dark:bg-[#111827]/95 border border-gray-100 dark:border-slate-800 p-6 rounded-[32px] shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-gray-955 dark:text-slate-200 uppercase tracking-wider">Top Recruiter Standings</h3>
            <span className="p-1 text-gray-400">
              <FiAward className="w-5 h-5 text-indigo-500" />
            </span>
          </div>
          <div className="h-56">
            {recruiterLeaderboardData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-xs text-gray-400">No placements logged yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={recruiterLeaderboardData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: "16px", border: "1px solid #f3f4f6", fontSize: "12px" }} />
                  <Bar dataKey="Placements" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

      </div>

      {/* ================================================= */}
      {/* 1. RECRUITER DRILL DOWN MODAL */}
      {/* ================================================= */}
      {selectedRecruiterDetails && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-[#111827] rounded-[32px] w-full max-w-[98vw] xl:max-w-[1550px] p-6 sm:p-8 shadow-2xl relative border border-gray-100 dark:border-slate-800 flex flex-col max-h-[90vh]">
            
            {/* Header info */}
            <div className="flex items-center justify-between border-b border-gray-150 dark:border-slate-800/60 pb-4 mb-6">
              <div>
                <h3 className="text-lg font-black text-gray-900 dark:text-slate-200 leading-none">
                  {selectedRecruiterDetails.recruiter.name} - Performance Diagnostic
                </h3>
                <p className="text-xs text-gray-400 dark:text-slate-500 font-bold mt-2 uppercase tracking-wide">
                  Designation: <span className="text-indigo-600 dark:text-indigo-400">{selectedRecruiterDetails.recruiter.designation}</span> &bull; 
                  Manager: <span className="text-indigo-650 dark:text-indigo-400">{selectedRecruiterDetails.recruiter.managerName}</span>
                </p>
              </div>
              <button
                onClick={() => setSelectedRecruiterDetails(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition text-gray-400 dark:text-slate-500 cursor-pointer"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Content areas */}
            <div className="flex-1 overflow-y-auto space-y-6 pr-2 no-scrollbar">
              
              {/* Recruiter specific mini KPI metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-850 p-4 rounded-2xl">
                  <span className="text-[10px] text-gray-400 dark:text-slate-500 uppercase font-bold tracking-wider">CVs Sourced</span>
                  <p className="text-base font-black text-gray-800 dark:text-slate-200 mt-1">{selectedRecruiterDetails.recruiter.sourced}</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-850 p-4 rounded-2xl">
                  <span className="text-[10px] text-gray-400 dark:text-slate-500 uppercase font-bold tracking-wider">Profiles Shared</span>
                  <p className="text-base font-black text-gray-800 dark:text-slate-200 mt-1">{selectedRecruiterDetails.recruiter.shared}</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-850 p-4 rounded-2xl">
                  <span className="text-[10px] text-gray-400 dark:text-slate-500 uppercase font-bold tracking-wider">Shortlisted</span>
                  <p className="text-base font-black text-gray-800 dark:text-slate-200 mt-1">{selectedRecruiterDetails.recruiter.shortlisted}</p>
                </div>
                <div className="bg-green-50/50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/30 p-4 rounded-2xl">
                  <span className="text-[10px] text-green-600 dark:text-green-400 uppercase font-bold tracking-wider">Total Closures</span>
                  <p className="text-base font-black text-green-700 dark:text-green-400 mt-1">{selectedRecruiterDetails.recruiter.placed} closures</p>
                </div>
              </div>

              {/* Side-by-side 50-50 Ratio Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Active Jobs Assigned */}
                <div className="space-y-3 min-w-0">
                  <h4 className="text-xs font-black text-gray-900 dark:text-slate-200 uppercase tracking-widest">Active Jobs Allocated</h4>
                  <div className="border border-gray-50 dark:border-slate-800 rounded-2xl max-h-[380px] overflow-y-auto overflow-x-auto">
                    <table className="w-full text-left text-[11px] font-bold text-gray-500 dark:text-slate-400">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-slate-900/40 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase sticky top-0 bg-white dark:bg-[#111827] z-10 whitespace-nowrap">
                          <th className="px-3 py-2.5">Req ID</th>
                          <th className="px-3 py-2.5">Client</th>
                          <th className="px-3 py-2.5">Position Title</th>
                          <th className="px-2 py-2.5 text-center">Openings</th>
                          <th className="px-2 py-2.5 text-center">Shared</th>
                          <th className="px-2 py-2.5 text-center">Shortlisted</th>
                          <th className="px-2 py-2.5 text-center">Joined</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-slate-800/40">
                        {selectedRecruiterDetails.jobs.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-4 text-gray-400 dark:text-slate-500 italic">No job allocations recorded</td>
                          </tr>
                        ) : (
                          selectedRecruiterDetails.jobs.map((job, idx) => (
                            <tr key={idx} className="whitespace-nowrap">
                              <td className="px-3 py-2.5 text-indigo-650 dark:text-indigo-400 font-bold">{job.req_id}</td>
                              <td className="px-3 py-2.5 text-gray-800 dark:text-slate-200">{job.company_name}</td>
                              <td className="px-3 py-2.5 text-gray-600 dark:text-slate-350">{job.position}</td>
                              <td className="px-2 py-2.5 text-center">{job.no_of_openings}</td>
                              <td className="px-2 py-2.5 text-center">{job.profiles_shared}</td>
                              <td className="px-2 py-2.5 text-center text-amber-600 dark:text-amber-400">{job.shortlisted || 0}</td>
                              <td className="px-2 py-2.5 text-center text-green-650 dark:text-green-400">{job.joined}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Placement closures list */}
                <div className="space-y-3 min-w-0">
                  <h4 className="text-xs font-black text-gray-900 dark:text-slate-200 uppercase tracking-widest">Closures Logged</h4>
                  <div className="border border-gray-50 dark:border-slate-800 rounded-2xl max-h-[380px] overflow-y-auto overflow-x-auto">
                    <table className="w-full text-left text-[11px] font-bold text-gray-500 dark:text-slate-400">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-slate-900/40 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase sticky top-0 bg-white dark:bg-[#111827] z-10 whitespace-nowrap">
                          <th className="px-3 py-2.5">Candidate Name</th>
                          <th className="px-3 py-2.5">Position</th>
                          <th className="px-3 py-2.5">Client Company</th>
                          <th className="px-3 py-2.5">DOJ</th>
                          <th className="px-3 py-2.5 text-right">Offered CTC</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-slate-800/40">
                        {selectedRecruiterDetails.placements.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-gray-400 dark:text-slate-500 italic">No closures recorded</td>
                          </tr>
                        ) : (
                          selectedRecruiterDetails.placements.map((p, idx) => (
                            <tr key={idx} className="whitespace-nowrap">
                              <td className="px-3 py-2.5 text-gray-800 dark:text-slate-200 font-bold">{p.candidate_name}</td>
                              <td className="px-3 py-2.5 text-gray-500 dark:text-slate-400">{p.position}</td>
                              <td className="px-3 py-2.5 text-gray-600 dark:text-slate-300 font-semibold">{p.company}</td>
                              <td className="px-3 py-2.5 text-gray-455 dark:text-slate-400">{p.doj && !isNaN(new Date(p.doj)) ? new Date(p.doj).toLocaleDateString("en-GB") : (p.doj || "-")}</td>
                              <td className="px-3 py-2.5 text-right text-gray-950 dark:text-slate-100 font-black">{formatCurrency(p.amount)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* 2. MANAGER DRILL DOWN MODAL */}
      {/* ================================================= */}
      {selectedManagerDetails && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-[#111827] rounded-[32px] w-full max-w-[98vw] xl:max-w-[1550px] p-6 sm:p-8 shadow-2xl relative border border-gray-100 dark:border-slate-800 flex flex-col max-h-[90vh]">
            
            {/* Header info */}
            <div className="flex items-center justify-between border-b border-gray-150 dark:border-slate-800/60 pb-4 mb-6">
              <div>
                <h3 className="text-lg font-black text-gray-900 dark:text-slate-200 leading-none">
                  Team {selectedManagerDetails.manager.name} - Performance Diagnostic
                </h3>
                <p className="text-xs text-gray-400 dark:text-slate-500 font-bold mt-2 uppercase tracking-wide">
                  Designation: <span className="text-indigo-600 dark:text-indigo-400">{selectedManagerDetails.manager.designation}</span> &bull; 
                  Total Team Members: <span className="text-indigo-650 dark:text-indigo-400">{selectedManagerDetails.manager.teamSize} Headcount</span>
                </p>
              </div>
              <button
                onClick={() => setSelectedManagerDetails(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition text-gray-400 dark:text-slate-500 cursor-pointer"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Content areas */}
            <div className="flex-1 overflow-y-auto space-y-6 pr-2 no-scrollbar">
              
              {/* Recruiter specific mini KPI metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-850 p-4 rounded-2xl">
                  <span className="text-[10px] text-gray-400 dark:text-slate-500 uppercase font-bold tracking-wider">Team Sourced</span>
                  <p className="text-base font-black text-gray-800 dark:text-slate-200 mt-1">{selectedManagerDetails.manager.sourced} CVs</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-850 p-4 rounded-2xl">
                  <span className="text-[10px] text-gray-400 dark:text-slate-500 uppercase font-bold tracking-wider">Team Profiles Shared</span>
                  <p className="text-base font-black text-gray-800 dark:text-slate-200 mt-1">{selectedManagerDetails.manager.shared} shared</p>
                </div>
                <div className="bg-green-50/50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/30 p-4 rounded-2xl">
                  <span className="text-[10px] text-green-600 dark:text-green-400 uppercase font-bold tracking-wider">Team Closures</span>
                  <p className="text-base font-black text-green-700 dark:text-green-400 mt-1">{selectedManagerDetails.manager.placed} Closures</p>
                </div>
                <div className="bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/30 p-4 rounded-2xl">
                  <span className="text-[10px] text-indigo-600 dark:text-indigo-400 uppercase font-bold tracking-wider">Team Revenue Generated</span>
                  <p className="text-base font-black text-indigo-700 dark:text-indigo-400 mt-1">{formatCurrency(selectedManagerDetails.manager.revenue)}</p>
                </div>
              </div>

              {/* Side-by-side 50-50 Ratio Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Direct Reporting Team Members */}
                <div className="space-y-3 min-w-0">
                  <h4 className="text-xs font-black text-gray-900 dark:text-slate-200 uppercase tracking-widest">Reporting Team Hierarchy</h4>
                  <div className="border border-gray-50 dark:border-slate-800 rounded-2xl max-h-[380px] overflow-y-auto overflow-x-auto">
                    <table className="w-full text-left text-[11px] font-bold text-gray-500 dark:text-slate-400">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-slate-900/40 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase sticky top-0 bg-white dark:bg-[#111827] z-10 whitespace-nowrap">
                          <th className="px-3 py-2.5">Member Name</th>
                          <th className="px-3 py-2.5">Code</th>
                          <th className="px-3 py-2.5">Designation</th>
                          <th className="px-3 py-2.5">Location</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-slate-800/40">
                        {selectedManagerDetails.reports.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center py-4 text-gray-400 dark:text-slate-500 italic">No reports registered under manager</td>
                          </tr>
                        ) : (
                          selectedManagerDetails.reports.map((member, idx) => (
                            <tr key={idx} className="whitespace-nowrap">
                              <td className="px-3 py-2.5 text-gray-955 dark:text-slate-200 font-bold">{member.name}</td>
                              <td className="px-3 py-2.5 text-indigo-650 dark:text-indigo-400">{member.employeeCode}</td>
                              <td className="px-3 py-2.5 text-gray-600 dark:text-slate-350 font-semibold">{member.designation}</td>
                              <td className="px-3 py-2.5 text-gray-400 dark:text-slate-500">{member.office}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Team placements closures list */}
                <div className="space-y-3 min-w-0">
                  <h4 className="text-xs font-black text-gray-900 dark:text-slate-200 uppercase tracking-widest">Team Closures Logged</h4>
                  <div className="border border-gray-50 dark:border-slate-800 rounded-2xl max-h-[380px] overflow-y-auto overflow-x-auto">
                    <table className="w-full text-left text-[11px] font-bold text-gray-500 dark:text-slate-400">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-slate-900/40 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase sticky top-0 bg-white dark:bg-[#111827] z-10 whitespace-nowrap">
                          <th className="px-3 py-2.5">Candidate Name</th>
                          <th className="px-3 py-2.5">Company</th>
                          <th className="px-3 py-2.5">DOJ</th>
                          <th className="px-3 py-2.5">Recruiter</th>
                          <th className="px-3 py-2.5 text-right">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-slate-800/40">
                        {selectedManagerDetails.placements.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-gray-400 dark:text-slate-500 italic">No closures logged by this team</td>
                          </tr>
                        ) : (
                          selectedManagerDetails.placements.map((p, idx) => (
                            <tr key={idx} className="whitespace-nowrap">
                              <td className="px-3 py-2.5 text-gray-850 dark:text-slate-200 font-bold">{p.candidate_name}</td>
                              <td className="px-3 py-2.5 text-gray-655 dark:text-slate-350">{p.company}</td>
                              <td className="px-3 py-2.5 text-gray-450 dark:text-slate-400">{p.doj && !isNaN(new Date(p.doj)) ? new Date(p.doj).toLocaleDateString("en-GB") : (p.doj || "-")}</td>
                              <td className="px-3 py-2.5 text-gray-650 dark:text-slate-350 font-bold">{p.recruiter_name}</td>
                              <td className="px-3 py-2.5 text-right text-gray-950 dark:text-slate-100 font-black">{formatCurrency(p.amount)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* KPI DRILL DOWN DETAILS MODALS */}
      {/* ================================================= */}
      {activeKpiModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-[#111827] rounded-[32px] w-full max-w-[95vw] lg:max-w-[1450px] p-6 sm:p-8 shadow-2xl relative border border-gray-100 dark:border-slate-800 flex flex-col max-h-[90vh]">
            
            {/* Header info */}
            <div className="flex items-center justify-between border-b border-gray-150 dark:border-slate-800/60 pb-4 mb-6">
              <div>
                <h3 className="text-lg font-black text-gray-900 dark:text-slate-200 leading-none capitalize">
                  {activeKpiModal === "revenue" ? "Closure Revenue Details" : 
                   activeKpiModal === "openings" ? "Active Job Openings Details" :
                   activeKpiModal === "sourced" ? "CVs Sourced Details" :
                   activeKpiModal === "interviews" ? "Interviews Tracked Details" :
                   "Conversion Ratio Details"}
                </h3>
                <p className="text-xs text-gray-400 dark:text-slate-500 font-bold mt-2 uppercase tracking-wide">
                  {activeKpiModal === "revenue" ? "Closures contributing to realized revenue" :
                   activeKpiModal === "openings" ? "Active positions currently in recruitment pipeline" :
                   activeKpiModal === "sourced" ? "Uploaded candidate resumes in selected range" :
                   activeKpiModal === "interviews" ? "Meetings and interview diagnostic log" :
                   "CV Share to closure conversion metrics"}
                </p>
              </div>
              <button
                onClick={() => setActiveKpiModal(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition text-gray-400 dark:text-slate-500 cursor-pointer"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Content areas */}
            <div className="flex-1 overflow-y-auto space-y-6 pr-2 no-scrollbar">
              <div className="overflow-x-auto border border-gray-50 dark:border-slate-800 rounded-2xl">
                {activeKpiModal === "revenue" && (
                  <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-slate-400">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-slate-900/40 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase whitespace-nowrap">
                        <th className="px-4 py-3">Candidate</th>
                        <th className="px-4 py-3">Client Company</th>
                        <th className="px-4 py-3">Position</th>
                        <th className="px-4 py-3">DOJ</th>
                        <th className="px-4 py-3 text-right">Offered CTC</th>
                        <th className="px-4 py-3 text-right">Revenue Generated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-slate-800/40">
                      {placementsListReport.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-6 text-gray-400 dark:text-slate-500 italic">No closure records registered</td>
                        </tr>
                      ) : (
                        placementsListReport.map((p, idx) => (
                          <tr key={idx} className="whitespace-nowrap">
                            <td className="px-4 py-3 text-gray-800 dark:text-slate-200">{p.candidate_name}</td>
                            <td className="px-4 py-3 text-gray-800 dark:text-slate-200">{p.company}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-slate-350">{p.designation || p.position}</td>
                            <td className="px-4 py-3">{p.doj ? new Date(p.doj).toLocaleDateString("en-GB") : "-"}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(p.ctc_offered)}</td>
                            <td className="px-4 py-3 text-right text-gray-950 dark:text-slate-100 font-black">{formatCurrency(p.amount)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}

                {activeKpiModal === "openings" && (
                  <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-slate-400">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-slate-900/40 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase whitespace-nowrap">
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Req ID</th>
                        <th className="px-4 py-3">Company</th>
                        <th className="px-4 py-3">Position</th>
                        <th className="px-4 py-3">Location</th>
                        <th className="px-4 py-3 text-center">Openings</th>
                        <th className="px-4 py-3 text-center">CVs Sourced</th>
                        <th className="px-4 py-3">Posted By</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 text-right">Budget</th>
                        <th className="px-4 py-3 text-right">Est. Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-slate-800/40">
                      {filteredPositionMatrix.filter(row => row.final_status === "Active").length === 0 ? (
                        <tr>
                          <td colSpan="11" className="text-center py-6 text-gray-400 dark:text-slate-500 italic">No active position matrix records found</td>
                        </tr>
                      ) : (
                        filteredPositionMatrix.filter(row => row.final_status === "Active").map((row, idx) => {
                          const userObj = teamMembers.find(t => Number(t.userId) === Number(row.user_id)) || usersList.find(u => Number(u.id) === Number(row.user_id));
                          const recruiterName = row.associate_name || row.recruiter_name || (userObj ? (userObj.name || `${userObj.first_name || ''} ${userObj.last_name || ''}`.trim()) : null);
                          const postedBy = row.posted_by || row.created_by || recruiterName || "Admin";
                          const formattedDate = row.position_received_on ? new Date(row.position_received_on).toLocaleDateString("en-GB") : "-";

                          const jobCandidates = filteredCandidates.filter(c => String(c.req_id) === String(row.req_id));
                          const cvSourcedCount = jobCandidates.length;

                          return (
                            <tr key={idx} className="whitespace-nowrap">
                              <td className="px-4 py-3 text-gray-500 dark:text-slate-400">{formattedDate}</td>
                              <td className="px-4 py-3 text-indigo-650 dark:text-indigo-400">{row.req_id || "-"}</td>
                              <td className="px-4 py-3 text-gray-800 dark:text-slate-200">{row.company_name}</td>
                              <td className="px-4 py-3 text-gray-600 dark:text-slate-350">{row.position}</td>
                              <td className="px-4 py-3">{row.location}</td>
                              <td className="px-4 py-3 text-center">{row.no_of_openings}</td>
                              <td className="px-4 py-3 text-center">
                                {cvSourcedCount > 0 ? (
                                  <button
                                    onClick={() => setSelectedJobCandidates({ job: row, candidates: jobCandidates })}
                                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline font-extrabold focus:outline-none"
                                  >
                                    {cvSourcedCount}
                                  </button>
                                ) : (
                                  <span className="text-gray-300 font-semibold">0</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-700 dark:text-slate-300">{postedBy}</td>
                              <td className="px-4 py-3">
                                <select
                                  value={row.final_status || "Active"}
                                  onChange={(e) =>
                                    handleStatusChange(row.req_id, e.target.value)
                                  }
                                  className="bg-gray-100 dark:bg-slate-800 border-none text-xs font-bold uppercase px-2 py-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-gray-700 dark:text-slate-350 cursor-pointer"
                                >
                                  <option value="Active">Active</option>
                                  <option value="Hold">Hold</option>
                                  <option value="Closed">Closed</option>
                                </select>
                              </td>
                              <td className="px-4 py-3 text-right">{formatCurrency(row.budget)}</td>
                              <td className="px-4 py-3 text-right text-gray-950 dark:text-slate-100 font-black">{formatCurrency(row.company_revenue)}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                )}

                {activeKpiModal === "sourced" && (
                  <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-slate-400">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-slate-900/40 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase whitespace-nowrap">
                        <th className="px-4 py-3">Candidate Name</th>
                        <th className="px-4 py-3">Req ID</th>
                        <th className="px-4 py-3">Contact Number</th>
                        <th className="px-4 py-3">Email Address</th>
                        <th className="px-4 py-3">Current Status</th>
                        <th className="px-4 py-3">Upload Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-slate-800/40">
                      {filteredCandidates.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-6 text-gray-400 dark:text-slate-500 italic">No candidates sourced</td>
                        </tr>
                      ) : (
                        filteredCandidates.map((c, idx) => (
                          <tr key={idx} className="whitespace-nowrap">
                            <td className="px-4 py-3 text-gray-800 dark:text-slate-200">{`${c.f_name || ""} ${c.l_name || ""}`}</td>
                            <td className="px-4 py-3 text-indigo-650 dark:text-indigo-400">{c.req_id || "-"}</td>
                            <td className="px-4 py-3">{c.phone_number || "-"}</td>
                            <td className="px-4 py-3">{c.email_id || "-"}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300">
                                {c.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">{c.uploaded_on ? new Date(c.uploaded_on).toLocaleDateString("en-GB") : "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}

                {activeKpiModal === "interviews" && (
                  <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-slate-400">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-slate-900/40 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase whitespace-nowrap">
                        <th className="px-4 py-3">Candidate Name</th>
                        <th className="px-4 py-3">Req ID</th>
                        <th className="px-4 py-3">Company</th>
                        <th className="px-4 py-3">Position</th>
                        <th className="px-4 py-3">Stage</th>
                        <th className="px-4 py-3">Interview Date</th>
                        <th className="px-4 py-3">Interview Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-slate-800/40">
                      {filteredInterviews.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-6 text-gray-400 dark:text-slate-500 italic">No active interviews tracked</td>
                        </tr>
                      ) : (
                        filteredInterviews.map((i, idx) => (
                          <tr key={idx} className="whitespace-nowrap">
                            <td className="px-4 py-3 text-gray-800 dark:text-slate-200">{i.candidate_name}</td>
                            <td className="px-4 py-3 text-indigo-650 dark:text-indigo-400">{i.req_id || "-"}</td>
                            <td className="px-4 py-3 text-gray-800 dark:text-slate-200">{i.company || "-"}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-slate-350">{i.position || "-"}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400">
                                {i.stage}
                              </span>
                            </td>
                            <td className="px-4 py-3">{i.interview_date ? new Date(i.interview_date).toLocaleDateString("en-GB") : "-"}</td>
                            <td className="px-4 py-3">{i.interview_time || "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}

                {activeKpiModal === "conversion" && (
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                      {/* Profiles Shared */}
                      <div className="bg-gray-50 dark:bg-slate-900/40 border border-gray-150 dark:border-slate-800 p-5 rounded-2xl text-center">
                        <span className="text-[10px] text-gray-400 dark:text-slate-500 uppercase font-bold tracking-wider">Profiles Shared</span>
                        <p className="text-2xl font-black text-gray-900 dark:text-slate-200 mt-2">
                          {kpis.profilesShared}
                        </p>
                        <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 block mt-1">Total Submissions</span>
                      </div>

                      {/* Joining / Conversion Rate */}
                      <div className="bg-green-50/50 dark:bg-green-950/10 border border-green-150 dark:border-green-900/30 p-5 rounded-2xl text-center">
                        <span className="text-[10px] text-green-600 dark:text-green-400 uppercase font-bold tracking-wider">Joining / Conversion</span>
                        <p className="text-2xl font-black text-green-700 dark:text-green-400 mt-2">{kpis.conversionRate}%</p>
                        <span className="text-[10px] font-bold text-green-600/80 dark:text-green-400/80 block mt-1">{kpis.placedCount} Candidates Joined</span>
                      </div>

                      {/* Rejection Rate */}
                      <div className="bg-rose-50/50 dark:bg-rose-950/10 border border-rose-150 dark:border-rose-900/30 p-5 rounded-2xl text-center">
                        <span className="text-[10px] text-rose-600 dark:text-rose-400 uppercase font-bold tracking-wider">Rejection Rate</span>
                        <p className="text-2xl font-black text-rose-700 dark:text-rose-400 mt-2">{kpis.rejectionRate}%</p>
                        <span className="text-[10px] font-bold text-rose-600/80 dark:text-rose-400/80 block mt-1">{kpis.rejectedCount} Candidates Rejected</span>
                      </div>

                      {/* Dropping Rate */}
                      <div className="bg-amber-50/50 dark:bg-amber-950/10 border border-amber-150 dark:border-amber-900/30 p-5 rounded-2xl text-center">
                        <span className="text-[10px] text-amber-600 dark:text-amber-400 uppercase font-bold tracking-wider">Dropping / Backout</span>
                        <p className="text-2xl font-black text-amber-700 dark:text-amber-400 mt-2">{kpis.droppingRate}%</p>
                        <span className="text-[10px] font-bold text-amber-600/80 dark:text-amber-400/80 block mt-1">{kpis.droppingCount} Backouts / No Shows</span>
                      </div>
                    </div>

                    {/* Calculation Breakdown */}
                    <div className="bg-white dark:bg-slate-900/20 border border-gray-100 dark:border-slate-800/80 rounded-2xl p-5 space-y-3">
                      <h4 className="text-xs font-bold text-gray-900 dark:text-slate-200 uppercase tracking-wider">Conversion &amp; Diagnostic Methodology</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-600 dark:text-slate-400">
                        <div className="bg-gray-50 dark:bg-slate-800/30 p-3.5 rounded-xl border border-gray-100 dark:border-slate-750">
                          <span className="font-bold text-green-600 dark:text-green-400 block mb-1">Joining Rate ({kpis.conversionRate}%)</span>
                          Calculated as <code className="bg-white dark:bg-slate-900 px-1 py-0.5 rounded text-[11px] font-bold">(Joined / Shared) &times; 100</code>.
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-800/30 p-3.5 rounded-xl border border-gray-100 dark:border-slate-750">
                          <span className="font-bold text-rose-600 dark:text-rose-400 block mb-1">Rejection Rate ({kpis.rejectionRate}%)</span>
                          Calculated as <code className="bg-white dark:bg-slate-900 px-1 py-0.5 rounded text-[11px] font-bold">(Rejections / Shared) &times; 100</code>.
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-800/30 p-3.5 rounded-xl border border-gray-100 dark:border-slate-750">
                          <span className="font-bold text-amber-600 dark:text-amber-400 block mb-1">Dropping Rate ({kpis.droppingRate}%)</span>
                          Calculated as <code className="bg-white dark:bg-slate-900 px-1 py-0.5 rounded text-[11px] font-bold">(Backouts &amp; No-Shows / Shared) &times; 100</code>.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* JOB CANDIDATES DRILL DOWN MODAL */}
      {/* ================================================= */}
      {selectedJobCandidates && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-[#111827] rounded-[32px] w-full max-w-[95vw] lg:max-w-[1450px] p-6 sm:p-8 shadow-2xl relative border border-gray-100 dark:border-slate-800 flex flex-col max-h-[90vh]">
            
            {/* Header info */}
            <div className="flex items-center justify-between border-b border-gray-150 dark:border-slate-800/60 pb-4 mb-6">
              <div>
                <h3 className="text-lg font-black text-gray-900 dark:text-slate-200 leading-none">
                  Candidates Sourced - Req ID {selectedJobCandidates.job.req_id}
                </h3>
                <p className="text-xs text-gray-400 dark:text-slate-500 font-bold mt-2 uppercase tracking-wide">
                  Position: <span className="text-indigo-600 dark:text-indigo-400">{selectedJobCandidates.job.position || selectedJobCandidates.job.job_title}</span> &bull; 
                  Client: <span className="text-indigo-650 dark:text-indigo-400">{selectedJobCandidates.job.company_name}</span> &bull; 
                  Total Sourced: <span className="text-green-600 dark:text-green-400">{selectedJobCandidates.candidates.length} Resumes</span>
                </p>
              </div>
              <button
                onClick={() => setSelectedJobCandidates(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition text-gray-400 dark:text-slate-500 cursor-pointer"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Candidate List Table */}
            <div className="flex-1 overflow-y-auto space-y-6 pr-2 no-scrollbar">
              <div className="overflow-x-auto border border-gray-50 dark:border-slate-800 rounded-2xl">
                <table className="w-full text-left text-xs font-bold text-gray-500 dark:text-slate-400">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-slate-900/40 border-b border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-slate-500 uppercase whitespace-nowrap">
                      <th className="px-4 py-3">Candidate Name</th>
                      <th className="px-4 py-3">Email Address</th>
                      <th className="px-4 py-3">Contact Number</th>
                      <th className="px-4 py-3">Current Company</th>
                      <th className="px-4 py-3">Designation</th>
                      <th className="px-4 py-3 text-center">Experience</th>
                      <th className="px-4 py-3 text-center">Notice Period</th>
                      <th className="px-4 py-3 text-right">Current / Expected CTC</th>
                      <th className="px-4 py-3">Sourced By</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Upload Date</th>
                      <th className="px-4 py-3 text-center">Resume</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-slate-800/40">
                    {selectedJobCandidates.candidates.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="text-center py-6 text-gray-400 dark:text-slate-500 italic">No candidates sourced for this position</td>
                      </tr>
                    ) : (
                      selectedJobCandidates.candidates.map((c, idx) => (
                        <tr key={idx} className="whitespace-nowrap">
                          <td className="px-4 py-3 text-gray-800 dark:text-slate-200 font-bold">{`${c.f_name || ""} ${c.l_name || ""}`}</td>
                          <td className="px-4 py-3">{c.email_id || "-"}</td>
                          <td className="px-4 py-3">{c.phone_number || "-"}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-slate-350">{c.current_company || "-"}</td>
                          <td className="px-4 py-3 text-gray-600 dark:text-slate-400">{c.designation || "-"}</td>
                          <td className="px-4 py-3 text-center">{c.experience ? `${c.experience} Years` : "-"}</td>
                          <td className="px-4 py-3 text-center">{c.notice_period || "-"}</td>
                          <td className="px-4 py-3 text-right">
                            {c.current_ctc || c.expected_ctc ? `${c.current_ctc || "-"} / ${c.expected_ctc || "-"}` : "-"}
                          </td>
                          <td className="px-4 py-3 text-indigo-650 dark:text-indigo-400">{c.uploaded_by || c.added_by || "-"}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300">
                              {c.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">{c.uploaded_on ? new Date(c.uploaded_on).toLocaleDateString("en-GB") : "-"}</td>
                          <td className="px-4 py-3 text-center">
                            {c.resume && c.resume !== "no_resume.pdf" ? (
                              <a
                                href={`${serverURL}${c.resume_path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline font-bold"
                              >
                                View Resume
                              </a>
                            ) : (
                              <span className="text-gray-400">No Resume</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
